import React, {
    FormEvent,
    KeyboardEvent,
    ReactElement, useEffect, useState,
} from "react";
import { Button } from "react-bootstrap";
import ReactDOM from "react-dom";
import { BiHeading, BiLink } from "react-icons/bi";
import { BsBlockquoteLeft, BsCheck2, BsCodeSquare } from "react-icons/bs";
import { GoBold, GoCode, GoItalic } from "react-icons/go";
import { GrOrderedList, GrStrikeThrough, GrUnorderedList } from "react-icons/gr";
import { IconType } from "react-icons/lib";
// TypeScript users only add this code
import {
    BaseSelection,
    Editor, Element as SlateElement, Node, Range, Text, Transforms,
} from "slate";
import { ReactEditor, useSlate } from "slate-react";
import {
    ElementSettings, ElementType, InlineHandler, StyleType, TextElement,
} from "./blocks/base";
import Styles from "./Editor.module.scss";
import KeyboardEventHandler from "./keyboard-event-handler";

const STYLE_BUTTONS: ({icon: IconType, format: StyleType, props?: Record<string, string>})[] = [
    { icon: GoBold, format: StyleType.BOLD },
    { icon: GoItalic, format: StyleType.ITALIC },
    { icon: GrStrikeThrough, format: StyleType.STRIKETHROUGH },
    { icon: GoCode, format: StyleType.CODE },

];

const ELEMENT_BUTTONS: ({icon: IconType, format: ElementType})[] = [

    { icon: GrUnorderedList, format: ElementType.UNORDERED_LIST },
    { icon: GrOrderedList, format: ElementType.ORDERED_LIST },
    { icon: BsBlockquoteLeft, format: ElementType.BLOCKQUOTE },
    { icon: BsCodeSquare, format: ElementType.CODE_BLOCK },
    { icon: BiHeading, format: ElementType.HEADER },

];

export function Portal({ children }: {children: ReactElement}): ReactElement | null {
    return typeof document === "object" ? ReactDOM.createPortal(children, document.body) : null;
}

const isFormatActive = (editor: ReactEditor, type: StyleType): boolean => {
    const [match] = Editor.nodes(editor, {
        match: item => !Editor.isEditor(item) && (item as TextElement)[type] != null,
        mode: "all",
    });
    return match != null;
};

export const toggleFormat = (editor: ReactEditor, type: StyleType, props: Record<string, string>): void => {
    const isActive = isFormatActive(editor, type);
    const handler = InlineHandler.get(type);
    if (!handler) {
        return;
    }
    Transforms.setNodes(
        editor,
        { [type]: (isActive ? undefined : handler.apply?.(props) ?? Object.create(null)) } as Record<StyleType, ElementSettings>,
        { match: Text.isText, split: true },
    );
};

function FormatButton({ format, icon, props }: {
    format: StyleType, icon: IconType, props: Record<string, string>
}) {
    const editor = useSlate();
    const Icon = icon;
    return (
        <Button
            variant=""
            className="text-white"
            active={isFormatActive(editor, format)}
            onClick={event => {
                event.preventDefault();
                toggleFormat(editor, format, props);
            }}
        >
            <Icon />
        </Button>
    );
}

const isBlockActive = (editor: ReactEditor, format: ElementType) => {
    const [match] = Editor.nodes(editor, {
        match: (item: Node) => !Editor.isEditor(item) && SlateElement.isElement(item) && item.type === format,
    });

    return match != null;
};

const TOGGLE_FORMATS = new Set([ElementType.ORDERED_LIST, ElementType.UNORDERED_LIST, ElementType.CODE_BLOCK]);
const toggleBlock = (editor: ReactEditor, format: ElementType) => {
    const isActive = isBlockActive(editor, format);
    const shouldRewrap = TOGGLE_FORMATS.has(format);

    Transforms.unwrapNodes(editor, {
        match: node => !Editor.isEditor(node) && SlateElement.isElement(node) && TOGGLE_FORMATS.has(node.type),
        split: true,
    });
    let newFormat = format;
    if (isActive) {
        newFormat = ElementType.PARAGRAPH;
    } else if (format === ElementType.ORDERED_LIST || format === ElementType.UNORDERED_LIST) {
        newFormat = ElementType.LIST_ITEM;
    }

    const newProperties: Partial<SlateElement> = {
        type: newFormat,
    };

    if (format === ElementType.CODE_BLOCK) {
        Transforms.unwrapNodes(editor, {
            match: node => !Editor.isEditor(node) && SlateElement.isElement(node) && node.type === ElementType.CODE_BLOCK,
        });
    } else {
        Transforms.setNodes(editor, newProperties);
    }

    if (!isActive && shouldRewrap) { //
        const block = { type: format, children: [] };
        Transforms.wrapNodes(editor, block);
    }
};

export const clearStyles = (editor: ReactEditor) => {
    const [match] = Editor.nodes(editor, {
        match: (item: Node) => !Editor.isEditor(item) && SlateElement.isElement(item) && item.type !== ElementType.PARAGRAPH,
    });

    if (match == null) {
        return;
    }

    Transforms.unwrapNodes(editor, {
        match: node => !Editor.isEditor(node) && SlateElement.isElement(node),
        split: true,
    });
    const newProperties: Partial<SlateElement> = {
        type: ElementType.PARAGRAPH,
    };

    Transforms.setNodes(editor, newProperties);
};

function BlockButton({ format, icon }: {format: ElementType, icon: IconType}) {
    const editor = useSlate();
    const Icon = icon;

    return (
        <Button
            variant=""
            className="text-white"
            active={isBlockActive(editor, format)}
            onClick={event => {
                event.preventDefault();
                toggleBlock(editor, format);
            }}
        >
            <Icon />
        </Button>
    );
}

let lastEmptyPara = false;
export const onDOMBeforeInput = (editor: Editor, event: InputEvent): void => {
    if (event.inputType === "formatBold") {
        toggleFormat(editor, StyleType.BOLD, {});
    } else if (event.inputType === "formatItalic") {
        toggleFormat(editor, StyleType.ITALIC, {});
    } else if (event.inputType === "formatStrikethrough") {
        toggleFormat(editor, StyleType.STRIKETHROUGH, {});
    } else if (event.inputType === "insertParagraph" && !isBlockActive(editor, ElementType.PARAGRAPH)) {
        if (lastEmptyPara) {
            toggleBlock(editor, ElementType.PARAGRAPH);
        } else {
            lastEmptyPara = true;
            return;
        }

        // editor.insertNode({
        //     text: "",
        // } as TextElement);
    } else {
        lastEmptyPara = false;
        return;
    }

    lastEmptyPara = false;
    event.preventDefault();
};

interface ToolbarProps {
    keyboardEvents: KeyboardEventHandler

}

const URL_REGEX = new RegExp("^(https?:\\/\\/)?" // protocol
+ "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" // domain name
+ "((\\d{1,3}\\.){3}\\d{1,3}))" // OR ip (v4) address
+ "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" // port and path
+ "(\\?[;&a-z\\d%_.~+=-]*)?" // query string
+ "(\\#[-a-z\\d_]*)?$", "i"); // fragment locator

export function Toolbar(props: ToolbarProps): ReactElement {
    const ref = React.createRef<HTMLDivElement>();
    const editor = useSlate() as ReactEditor;

    const linkRef = React.createRef<HTMLInputElement>();
    const [isLinkCreate, setIsLinkCreate] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");
    const [linkSelection, setLinkSelection] = useState<BaseSelection>();

    const startLink = (event?: KeyboardEvent) => {
        event?.preventDefault();
        const text = editor.selection ? Editor.string(editor, editor.selection) : undefined;
        if (text && URL_REGEX.test(text)) {
            const currentSelection = editor.selection;

            if (currentSelection) {
                Transforms.insertText(editor, " ", currentSelection ? { at: Editor.end(editor, currentSelection) } : undefined);
                editor.selection = currentSelection;
            }

            toggleFormat(editor, StyleType.LINK, { url: text });
            Transforms.collapse(editor, { edge: "end" });
            Transforms.move(editor);
            return;
        }

        setLinkSelection(editor.selection);
        setIsLinkCreate(true);
    };
    props.keyboardEvents.useShortcuts(handler => {
        handler.on("ctrl+k", startLink);
        handler.on("cmd+k", startLink);
    }, []);
    useEffect(() => {
        const el = ref.current;
        const { selection } = editor;

        if (!el) {
            return;
        }

        if (isLinkCreate) {
            linkRef.current?.focus();
            return;
        }

        if (!selection
            // || (!ReactEditor.isFocused(editor))
            || Range.isCollapsed(selection)
            || Editor.string(editor, selection) === "") {
            el.removeAttribute("style");
            return;
        }

        const domSelection = window.getSelection();
        if (!domSelection) {
            return;
        }
        const domRange = domSelection.getRangeAt(0);
        const rect = domRange.getBoundingClientRect();
        el.style.opacity = "1";
        el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight}px`;
        el.style.left = `${rect.left + window.pageXOffset - el.offsetWidth / 2 + rect.width / 2}px`;
    });

    const createLink = (event: FormEvent) => {
        event.preventDefault();
        event?.stopPropagation();
        ReactEditor.focus(editor);

        if (linkSelection) {
            Transforms.insertText(editor, " ", { at: Editor.end(editor, linkSelection) });
            editor.selection = linkSelection;
        }

        toggleFormat(editor, StyleType.LINK, { url: linkUrl });
        setLinkUrl("");
        setIsLinkCreate(false);

        Transforms.collapse(editor, { edge: "end" });
        Transforms.move(editor);
    };

    const linkKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape") {
            event.stopPropagation();
            setLinkUrl("");
            setIsLinkCreate(false);
        }
    };

    return (
        <Portal>
            <div
                ref={ref}
                className={Styles.toolbar}
            >
                {isLinkCreate ? (
                    <form className="d-flex bg-none" onSubmit={createLink}>
                        <input
                            ref={linkRef}
                            type="url"
                            required
                            className={`border-0 text-white ${Styles.input}`}
                            onChange={evt => setLinkUrl(evt.target.value)}
                            value={linkUrl}
                            placeholder="https://foqal.io."
                            onKeyDown={linkKeyDown}
                        />
                        <button
                            type="submit"
                            className="btn btn-sm  text-white p-2"
                        >
                            <BsCheck2 className="fs-5" />
                        </button>
                    </form>
                ) : (
                    <>
                        {STYLE_BUTTONS.map(btn => (
                            <FormatButton
                                key={btn.format}
                                format={btn.format}
                                icon={btn.icon}
                                props={btn.props ?? {}}
                            />
                        ))}
                        <Button
                            variant=""
                            className="text-white"
                            active={isFormatActive(editor, StyleType.LINK)}
                            onClick={event => {
                                event.preventDefault();
                                if (isFormatActive(editor, StyleType.LINK)) {
                                    toggleFormat(editor, StyleType.LINK, { });
                                } else {
                                    startLink();
                                }
                            }}
                        >
                            <BiLink />
                        </Button>
                        {ELEMENT_BUTTONS.map(btn => <BlockButton key={btn.format} format={btn.format} icon={btn.icon} />)}

                    </>
                )}

            </div>
        </Portal>

    );
}
